/**
 * What this user is worth, so a four-year customer and a one-month trial are
 * comparable at a glance.
 *
 * Two numbers, deliberately separate:
 * - **run rate**: what the live subscriptions come to per month
 * - **lifetime**: what they have actually paid, minus refunds
 *
 * Money from a subscription cancelled in the last {@link REFUND_WINDOW_DAYS}
 * days is held back rather than counted: those are usually refunded, and a
 * FastSpring refund is invisible to us, so counting it would report revenue we
 * are about to give back.
 *
 * Currencies are NEVER combined. This account base bills in USD, AUD and EUR —
 * adding them would produce a confident, wrong number, and we hold no FX rates.
 * Each currency is reported on its own line.
 */
import type { BillingSnapshot } from "./user-management.js";

export interface MoneyByCurrency {
  currency: string;
  amount: number;
}

/**
 * How long after a cancellation a refund is still likely. Money from
 * subscriptions cancelled inside this window is reported separately, not as
 * revenue.
 */
export const REFUND_WINDOW_DAYS = 35;

export interface AccountRevenue {
  /** Live subscriptions normalised to a monthly figure, per currency. */
  runRate: MoneyByCurrency[];
  /** Charges minus refunds, per currency. Excludes {@link atRisk}. */
  lifetime: MoneyByCurrency[];
  /**
   * Charges for subscriptions cancelled within the refund window — probably
   * refunded, so held out of `lifetime` and shown separately.
   */
  atRisk: MoneyByCurrency[];
  /** Months between the first payment and now — the "how long" half. */
  monthsAsCustomer: number | null;
  firstPayment: string | null;
}

const ACTIVE_STATES = ["active", "trialing", "past_due"];

/** "1 year" / "3 months" / "30 days" → months, or null if unparseable. */
export function intervalInMonths(interval: string | null): number | null {
  if (!interval) return null;
  const m = /^(\d+)\s*(day|week|month|year)s?$/i.exec(interval.trim());
  if (!m) return null;
  const n = Number(m[1]);
  switch (m[2].toLowerCase()) {
    case "day":
      return (n * 12) / 365;
    case "week":
      return (n * 12) / 52;
    case "month":
      return n;
    case "year":
      return n * 12;
    default:
      return null;
  }
}

function add(into: Map<string, number>, currency: string | null, amount: number) {
  const key = (currency ?? "USD").toUpperCase();
  into.set(key, (into.get(key) ?? 0) + amount);
}

function toList(map: Map<string, number>): MoneyByCurrency[] {
  return [...map.entries()]
    .map(([currency, amount]) => ({ currency, amount: Math.round(amount * 100) / 100 }))
    .filter((m) => m.amount !== 0)
    .sort((a, b) => b.amount - a.amount);
}

export function accountRevenue(
  snapshots: BillingSnapshot[],
  now: number = Date.now()
): AccountRevenue {
  const run = new Map<string, number>();
  const life = new Map<string, number>();
  const risk = new Map<string, number>();
  let first: string | null = null;

  // Subscriptions cancelled recently enough that a refund is still likely.
  const recentlyCancelled = new Set<string>();
  for (const snap of snapshots) {
    if (!snap.available) continue;
    for (const sub of snap.subscriptions) {
      if (!sub.cancelledDate) continue;
      const days = (now - new Date(sub.cancelledDate).getTime()) / 86_400_000;
      if (days >= 0 && days <= REFUND_WINDOW_DAYS) recentlyCancelled.add(sub.id);
    }
  }

  for (const snap of snapshots) {
    if (!snap.available) continue;

    for (const sub of snap.subscriptions) {
      if (!ACTIVE_STATES.includes(sub.status.toLowerCase())) continue;
      const months = intervalInMonths(sub.interval);
      if (sub.amount == null || !months) continue;
      add(run, sub.currency, sub.amount / months);
    }

    for (const e of snap.events) {
      if (e.amount == null) continue;
      if (e.type === "charge") {
        // Every charge counts at face value; the refund row below takes the
        // money back. Skipping a charge *because* it is marked refunded, while
        // also subtracting its refund, would double-count the reversal — a
        // fully refunded customer then reads as NEGATIVE lifetime revenue.
        if (e.subscriptionId && recentlyCancelled.has(e.subscriptionId)) {
          add(risk, e.currency, e.amount);
        } else {
          add(life, e.currency, e.amount);
        }
        if (e.date && (first == null || e.date < first)) first = e.date;
      } else if (e.type === "refund") {
        add(life, e.currency, -e.amount);
      }
    }
  }

  const monthsAsCustomer = first
    ? Math.max(
        0,
        Math.round(
          ((Date.now() - new Date(first).getTime()) / (365.25 * 86_400_000)) * 12
        )
      )
    : null;

  return {
    runRate: toList(run),
    lifetime: toList(life),
    atRisk: toList(risk),
    monthsAsCustomer,
    firstPayment: first,
  };
}
